import { useEffect, useState, type MouseEvent } from 'react';
import useSWR from 'swr';
import { useAtomValue } from 'jotai';
import { usePathname } from 'next/navigation';
import { setCookie } from 'nookies';

import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import type { CookieOptions } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getFullLocaleFromConfig, localizedPathnameCacheKey } from '@packages/config/src/default';
import type { Language } from '@packages/config';
import { localizeUrl } from '@packages/shared/src/utils/localizeUrl/localizeUrl';

import { languageAlternatesAtom } from './useLocaleSwitch.atom';

export const useLocaleSwitch = () => {
  const [showUrl, setShowUrl] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  // Hide the URL on initial SSR, because the page-provided alternates are only available on the client when using the app router
  // If we show the default language-changed pathname, it will be incorrect for some routes, and possibly lead to SEO index pollution
  // With this delay, we allow the page enough time to set the atom with the correct alternates
  useEffect(() => {
    setShowUrl(true);
  }, []);

  const config = useConfig();
  const {
    i18n: { languages },
  } = useConfig();
  const { language } = useI18n();
  const pathname = usePathname();
  const url = useUrl();

  useEffect(() => {
    setIsSwitching(false);
  }, [language]);

  const { data: localizedPathnameFromPagesRouter } = useSWR(localizedPathnameCacheKey, {
    // prevent fetching newer data and use only stale data (https://swr.vercel.app/docs/revalidation#disable-automatic-revalidations)
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  // TODO SEARCH-3560 re-evaluate cookie options, some of them seem to be outdated
  const COOKIE_OPTIONS: CookieOptions = {
    path: '/',
    maxAge: 90 * 24 * 60 * 60, // 90 days in seconds
    secure: false, // true would be nice but this is what intershop defines,
    sameSite: 'lax',
    httpOnly: false,
  };

  const languageTitle: Partial<Record<Language, string>> = {
    fr: 'Français',
    de: 'Deutsch',
  };

  const targetLanguage: Language = languages.find((l: string) => l !== language) ?? languages[0];

  const languageAlternates = useAtomValue(languageAlternatesAtom);
  const localizedPathnameFromAppRouter =
    languageAlternates?.currentPathname === pathname
      ? languageAlternates?.alternateLinks?.[targetLanguage]
      : undefined;

  const href = showUrl
    ? localizeUrl(
        localizedPathnameFromAppRouter || localizedPathnameFromPagesRouter || url,
        targetLanguage,
        config,
      )
    : undefined;

  const switchLocale = (e?: MouseEvent) => {
    e?.preventDefault();
    setIsSwitching(true);
    setCookie(null, 'PreferredLanguage', targetLanguage, COOKIE_OPTIONS);
    // Set PreferredLocale with legacy parameters for consistent behaviour with the legacy checkout.
    // Can be removed after nextjs checkout is live
    setCookie(
      null,
      'PreferredLocale',
      getFullLocaleFromConfig(targetLanguage, config).replace('-', '_'),
      {
        ...COOKIE_OPTIONS,
      },
    );
    if (href) {
      window.location.replace(href);
    }
  };

  return {
    label: languageTitle[targetLanguage],
    targetLanguage,
    href,
    switchLocale,
    isSwitching,
  };
};
